import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useThreadStore } from "@/store";
import { Check, ChevronDown } from "lucide-react";
import PropTypes from "prop-types";
import { forwardRef, useCallback, useState } from "react";
import {
  FOCUS_ICON_MAP,
  WEB_SEARCH_FOCUS_OPTIONS,
  WEB_SEARCH_FOCUS_TRANSLATIONS,
} from "@/lib/constants/webSearchFocus";

/**
 * WebSearchFocus Component
 *
 * Displays a dropdown to select web search focus categories.
 * Options include Auto, Company, Research, News, PDF, GitHub, Personal Sites, and LinkedIn.
 * Each option filters search results to specific content types.
 *
 * The selected focus is stored in thread metadata and sent to the API as the 'category' parameter.
 */
const WebSearchFocus = forwardRef(({
  selectedWebSearchFocus = "auto",
  onFocusChange,
  safeT
}, ref) => {
  const [open, setOpen] = useState(false);

  // Safe translation function - uses provided safeT or falls back to built-in translations
  const translate = useCallback(
    (key, fallback) => {
      // Try provided translation function first
      if (safeT) {
        const translated = safeT(key, fallback);
        if (translated && translated !== key) {
          return translated;
        }
      }
      // Use built-in translations from the constants file
      return WEB_SEARCH_FOCUS_TRANSLATIONS[key] || fallback || key;
    },
    [safeT]
  );

  // Handle focus option selection
  const handleSelect = useCallback(
    (option) => {
      setOpen(false);

      // Update thread metadata
      const metadata = useThreadStore.getState().metadata || {};
      useThreadStore.setState({
        metadata: { ...metadata, webSearchFocus: option.id },
      });

      // Notify parent component
      if (onFocusChange) {
        onFocusChange(option.id, {
          focus: option.id,
          focusDescription: translate(option.descriptionKey, option.descriptionKey),
        });
      }
    },
    [onFocusChange, translate]
  );

  // Find current selected option
  const selectedOption =
    WEB_SEARCH_FOCUS_OPTIONS.find((opt) => opt.id === selectedWebSearchFocus) ||
    WEB_SEARCH_FOCUS_OPTIONS[0];
  const SelectedIcon = FOCUS_ICON_MAP[selectedOption?.icon] || FOCUS_ICON_MAP.auto;

  return (
    <TooltipProvider delayDuration={300}>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                ref={ref}
                variant="ghost"
                size="sm"
                className={cn(
                  "flex items-center gap-1.5 h-8 px-2",
                  "bg-transparent hover:bg-zinc-100 dark:hover:bg-zinc-800",
                  "text-zinc-700 dark:text-zinc-300",
                  "rounded-md cursor-pointer transition-all duration-200"
                )}
              >
                <SelectedIcon className="!h-4 !w-4 text-zinc-700 dark:text-zinc-300" />
                <span className="hidden sm:inline text-sm font-medium text-zinc-700 dark:text-zinc-300">
                  {translate(selectedOption?.nameKey, selectedWebSearchFocus)}
                </span>
                <ChevronDown className="h-2.5 w-2.5 text-zinc-700 dark:text-zinc-300 ml-1" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent
            align="start"
            className={cn(
              "bg-white dark:bg-zinc-900",
              "border border-zinc-200 dark:border-zinc-700",
              "text-zinc-900 dark:text-zinc-100",
              "px-3 py-2 rounded-md shadow-lg max-w-xs"
            )}
          >
            <p>{translate(selectedOption?.descriptionKey, selectedOption?.descriptionKey || "")}</p>
          </TooltipContent>
        </Tooltip>

        <DropdownMenuContent
          className={cn(
            "w-48 p-1 bg-white/70 dark:bg-zinc-900/65",
            "border border-gray-200/70 dark:border-zinc-700/70",
            "backdrop-blur-md shadow-lg shadow-black/10 rounded-xl"
          )}
          align="end"
          side="bottom"
          sideOffset={8}
          style={{ zIndex: 400 }}
        >
          <div className="space-y-0">
            <TooltipProvider delayDuration={300}>
              {WEB_SEARCH_FOCUS_OPTIONS.map((option) => {
                const OptionIcon = FOCUS_ICON_MAP[option.icon] || FOCUS_ICON_MAP.auto;
                const isSelected = selectedWebSearchFocus === option.id;

                return (
                  <Tooltip key={option.id}>
                    <TooltipTrigger asChild>
                      <DropdownMenuItem
                        onSelect={() => handleSelect(option)}
                        className={cn(
                          "w-full flex items-center gap-2 px-2 py-1.5",
                          "transition-all duration-150 text-left cursor-pointer",
                          isSelected
                            ? "bg-[#2563eb]/10 dark:bg-[#99CEFF]/10 text-[#2563eb] dark:text-[#99CEFF]"
                            : "bg-transparent hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-700 dark:text-zinc-300"
                        )}
                      >
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                          <OptionIcon className="w-4 h-4" />
                        </div>
                        <span className="flex-1 truncate text-sm">
                          {translate(option.nameKey, option.nameKey)}
                        </span>
                        {isSelected && (
                          <Check className="h-3 w-3 text-[#2563eb] dark:text-[#99CEFF] flex-shrink-0" />
                        )}
                      </DropdownMenuItem>
                    </TooltipTrigger>
                    <TooltipContent
                      side="left"
                      align="center"
                      className={cn(
                        "bg-white dark:bg-zinc-900",
                        "border border-zinc-200 dark:border-zinc-700",
                        "text-zinc-900 dark:text-zinc-100",
                        "px-3 py-2 rounded-md shadow-lg max-w-xs"
                      )}
                    >
                      <p className="text-sm">
                        {translate(option.descriptionKey, option.descriptionKey)}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </TooltipProvider>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </TooltipProvider>
  );
});

WebSearchFocus.displayName = "WebSearchFocus";

WebSearchFocus.propTypes = {
  selectedWebSearchFocus: PropTypes.string,
  onFocusChange: PropTypes.func,
  safeT: PropTypes.func,
};

export default WebSearchFocus;
